import { memo, useCallback, useMemo, useState } from 'react';
import { Button, SegmentedControl, Tab, TabNav } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { KeyRound } from 'lucide-react';

import { ASSIGNABLE_USER_TYPES } from '../../../constants/accessRules.js';
import { normalizeAccess } from '../../../utils/accessRules.js';
import ImportDialog from '../../shared/ImportDialog.jsx';
import { groupItems, roleItems, toggleId } from './accessLists.js';
import CatalogChecklist from './CatalogChecklist.jsx';

const TABS = ['roles', 'groups'];

const namesOf = (items, ids) => items.filter((item) => ids.includes(item.id)).map((item) => item.name);

/**
 * "Assign access to N people": the same access for everyone selected, whatever their row says;
 * people who are already members keep theirs (the reducer skips them). Mount it only while
 * open so every opening starts from a clean draft.
 *
 * @param {object} props
 * @param {number} props.count People selected.
 * @param {Array<{ id: string, name: string, studios: string[] }>} props.roles
 * @param {Array<{ id: string, name: string }>} props.groups
 * @param {(access: { user_type: string, role_ids: string[], group_ids: string[] }) => void} props.onConfirm
 * @param {() => void} props.onClose
 */
function AssignAccessModal({ count, roles, groups, onConfirm, onClose }) {
  const { t } = useTranslation('people-access');
  const [tab, setTab] = useState(TABS[0]);
  const [draft, setDraft] = useState({ user_type: ASSIGNABLE_USER_TYPES[0], role_ids: [], group_ids: [] });
  const roleOptions = useMemo(() => roleItems(roles), [roles]);
  const groupOptions = useMemo(() => groupItems(groups), [groups]);
  const userTypes = useMemo(
    () => ASSIGNABLE_USER_TYPES.map((value) => ({ value, label: t(`terms.userType.${value}`) })),
    [t],
  );
  const givenRoles = namesOf(roleOptions, draft.role_ids);
  const givenGroups = namesOf(groupOptions, draft.group_ids);

  const handleUserType = useCallback((user_type) => setDraft((d) => ({ ...d, user_type })), []);
  const handleRole = useCallback(
    (id) => setDraft((d) => ({ ...d, role_ids: toggleId(d.role_ids, id) })),
    [],
  );
  const handleGroup = useCallback(
    (id) => setDraft((d) => ({ ...d, group_ids: toggleId(d.group_ids, id) })),
    [],
  );
  // A draft that normalizes to "no access" (a Member without a role) would clear the access of
  // everyone selected, so it cannot be confirmed.
  const canConfirm = normalizeAccess(draft) !== null;
  const handleConfirm = useCallback(() => onConfirm(draft), [onConfirm, draft]);

  return (
    <ImportDialog
      onClose={onClose}
      titleId="assign-access-title"
      icon={
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent">
          <KeyRound size={20} aria-hidden />
        </span>
      }
      title={t('import.review.assign.title', { count })}
      subtitle={t('import.review.assign.subtitle')}
      footer={
        <>
          {canConfirm ? null : (
            <p className="mr-auto text-xs text-content-secondary">{t('import.review.assign.needsRole')}</p>
          )}
          <Button variant="secondary" customButtonHeight="!h-[40px]" className="!px-6" data-autofocus onClick={onClose}>
            {t('import.cancel')}
          </Button>
          <Button variant="primary" customButtonHeight="!h-[40px]" className="!px-6" disabled={!canConfirm} onClick={handleConfirm}>
            {t('import.review.assign.confirm')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 py-2">
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold text-content-secondary">{t('import.review.assign.userType')}</p>
          <div className="w-fit">
            <SegmentedControl
              value={draft.user_type}
              options={userTypes}
              onChange={handleUserType}
              ariaLabel={t('import.review.assign.userType')}
            />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold text-content-secondary">{t('import.review.assign.giving')}</p>
          <dl className="m-0 flex flex-col gap-3 text-sm">
            <div className="grid grid-cols-[10rem_1fr] gap-4">
              <dt className="flex flex-col">
                <span className="font-semibold text-content">{t('import.review.assign.rolesLabel')}</span>
                <span className="text-xs text-content-muted">{t('import.review.assign.rolesHint')}</span>
              </dt>
              <dd className="m-0 text-content-secondary">
                {givenRoles.length > 0 ? givenRoles.join(', ') : t('import.review.assign.noRoles')}
              </dd>
            </div>
            <div className="grid grid-cols-[10rem_1fr] gap-4">
              <dt className="flex flex-col">
                <span className="font-semibold text-content">{t('import.review.assign.groupsLabel')}</span>
                <span className="text-xs text-content-muted">{t('import.review.assign.groupsHint')}</span>
              </dt>
              <dd className="m-0 text-content-secondary">
                {givenGroups.length > 0 ? givenGroups.join(', ') : t('import.review.assign.noGroups')}
              </dd>
            </div>
          </dl>
        </div>
        <TabNav ariaLabel={t('import.review.assign.tabsLabel')}>
          {TABS.map((id) => (
            <Tab key={id} selected={tab === id} onClick={() => setTab(id)}>
              {t(`import.review.assign.tab${id[0].toUpperCase()}${id.slice(1)}`)}
            </Tab>
          ))}
        </TabNav>
        {tab === 'roles' ? (
          <CatalogChecklist
            items={roleOptions}
            selectedIds={draft.role_ids}
            onToggle={handleRole}
            searchLabel={t('import.review.popover.rolesSearch')}
            showCount
          />
        ) : null}
        {tab === 'groups' ? (
          <CatalogChecklist
            items={groupOptions}
            selectedIds={draft.group_ids}
            onToggle={handleGroup}
            searchLabel={t('import.review.popover.groupsSearch')}
            showCount
          />
        ) : null}
      </div>
    </ImportDialog>
  );
}

export default memo(AssignAccessModal);
